import { keyedEnum } from '../catalogs/enum.js';
/**
 * Ciclo de vida del préstamo. Las etapas de originación reflejan cómo una IMF
 * guatemalteca trabaja un expediente: requisitos, avalúo (solo con garantía
 * real), estudio financiero, verificación de campo, comité,
 * formalización/desembolso y cartera. La mora *no* es un estado: se deriva de
 * los días de atraso.
 */
export const LOAN_STATUSES = [
    'application',
    'document_review',
    'appraisal',
    'credit_analysis',
    'field_verification',
    'committee',
    'approved',
    'active',
    'paid',
    'written_off',
    'rejected',
    'cancelled',
];
/** Catálogo con clave de estados de préstamo: `LOAN_STATUS.active`. */
export const LOAN_STATUS = keyedEnum(LOAN_STATUSES);
/** Etiquetas en español de cada estado. */
export const LOAN_STATUS_LABELS = {
    application: 'Solicitud ingresada',
    document_review: 'Revisión de requisitos',
    appraisal: 'Avalúo de garantía',
    credit_analysis: 'Estudio financiero',
    field_verification: 'Verificación de campo',
    committee: 'Comité de crédito',
    approved: 'Aprobado · por desembolsar',
    active: 'Vigente',
    paid: 'Cancelado (pagado)',
    written_off: 'Castigado',
    rejected: 'Rechazado',
    cancelled: 'Anulado',
};
/** Estados de un préstamo aún en originación (el pipeline / Kanban). */
export const ORIGINATION_STATUSES = [
    'application',
    'document_review',
    'appraisal',
    'credit_analysis',
    'field_verification',
    'committee',
    'approved',
];
/** Estados de un préstamo que tiene saldo (la cartera). */
export const PORTFOLIO_STATUSES = ['active'];
/** Estados finales: el préstamo ya no cambia. */
export const CLOSED_STATUSES = ['paid', 'written_off', 'rejected', 'cancelled'];
/** Indica si el estado pertenece a la originación. */
export function isOriginationStatus(status) {
    return ORIGINATION_STATUSES.includes(status);
}
/**
 * Cómo se dispara una transición: `manual` por el endpoint genérico de
 * transición, o mediante una acción dedicada que registra más datos.
 */
export const TRANSITION_TRIGGERS = ['manual', 'committee_decision', 'disbursement', 'payment', 'write_off'];
/** Catálogo con clave de disparadores: `TRANSITION_TRIGGER.manual`. */
export const TRANSITION_TRIGGER = keyedEnum(TRANSITION_TRIGGERS);
/** Etapas previas al comité: desde ellas se puede declinar la solicitud. */
const PRE_COMMITTEE = [
    LOAN_STATUS.application,
    LOAN_STATUS.document_review,
    LOAN_STATUS.appraisal,
    LOAN_STATUS.credit_analysis,
    LOAN_STATUS.field_verification,
];
/** Devuelve los mensajes de las condiciones que no se cumplen. */
function check(conditions) {
    return conditions.filter(([ok]) => !ok).map(([, message]) => message);
}
/** Tabla completa de transiciones permitidas, con permiso y requisitos. */
export const TRANSITIONS = [
    {
        from: LOAN_STATUS.application,
        to: LOAN_STATUS.document_review,
        label: 'Enviar a revisión de requisitos',
        permission: 'loans:originate',
        trigger: TRANSITION_TRIGGER.manual,
    },
    {
        from: LOAN_STATUS.document_review,
        to: LOAN_STATUS.appraisal,
        label: 'Enviar a avalúo',
        permission: 'loans:originate',
        trigger: TRANSITION_TRIGGER.manual,
        missing: (f) => check([
            [f.requiresCollateral, 'El préstamo no tiene garantía real; envíalo directo a estudio financiero'],
            [f.hasIdentityDocument, 'Falta adjuntar el DPI del solicitante'],
        ]),
    },
    {
        from: LOAN_STATUS.document_review,
        to: LOAN_STATUS.credit_analysis,
        label: 'Enviar a estudio financiero',
        permission: 'loans:originate',
        trigger: TRANSITION_TRIGGER.manual,
        missing: (f) => check([
            [!f.requiresCollateral, 'El préstamo requiere avalúo de garantía antes del estudio'],
            [f.hasIdentityDocument, 'Falta adjuntar el DPI del solicitante'],
            [!f.requiresGuarantor || f.hasGuarantor, 'El producto requiere al menos un fiador'],
        ]),
    },
    {
        from: LOAN_STATUS.appraisal,
        to: LOAN_STATUS.credit_analysis,
        label: 'Avalúo completo: enviar a estudio',
        permission: 'loans:appraise',
        trigger: TRANSITION_TRIGGER.manual,
        missing: (f) => check([
            [f.hasAppraisedCollateral, 'Registra el avalúo de al menos una garantía'],
            [f.collateralCovered, 'Las garantías no cubren la cobertura mínima del producto'],
        ]),
    },
    {
        from: LOAN_STATUS.credit_analysis,
        to: LOAN_STATUS.field_verification,
        label: 'Enviar a verificación de campo',
        permission: 'loans:analyze',
        trigger: TRANSITION_TRIGGER.manual,
        missing: (f) => check([[f.hasFinancialAssessment, 'Registra el estudio financiero']]),
    },
    {
        from: LOAN_STATUS.field_verification,
        to: LOAN_STATUS.committee,
        label: 'Presentar a comité',
        permission: 'loans:analyze',
        trigger: TRANSITION_TRIGGER.manual,
        missing: (f) => check([
            [f.hasFieldVerification, 'Registra la verificación de campo'],
            [!f.requiresGuarantor || f.hasGuarantor, 'El producto requiere al menos un fiador'],
        ]),
    },
    {
        from: LOAN_STATUS.committee,
        to: LOAN_STATUS.credit_analysis,
        label: 'Devolver a estudio financiero',
        permission: 'loans:decide',
        trigger: TRANSITION_TRIGGER.manual,
    },
    { from: LOAN_STATUS.committee, to: LOAN_STATUS.approved, label: 'Aprobar', permission: 'loans:decide', trigger: TRANSITION_TRIGGER.committee_decision },
    { from: LOAN_STATUS.committee, to: LOAN_STATUS.rejected, label: 'Rechazar', permission: 'loans:decide', trigger: TRANSITION_TRIGGER.committee_decision },
    { from: LOAN_STATUS.approved, to: LOAN_STATUS.active, label: 'Desembolsar', permission: 'loans:disburse', trigger: TRANSITION_TRIGGER.disbursement },
    { from: LOAN_STATUS.active, to: LOAN_STATUS.paid, label: 'Cancelación total', permission: 'payments:post', trigger: TRANSITION_TRIGGER.payment },
    { from: LOAN_STATUS.active, to: LOAN_STATUS.written_off, label: 'Castigar', permission: 'loans:write_off', trigger: TRANSITION_TRIGGER.write_off },
    ...PRE_COMMITTEE.map((from) => ({
        from,
        to: LOAN_STATUS.rejected,
        label: 'Declinar solicitud',
        permission: 'loans:analyze',
        trigger: TRANSITION_TRIGGER.manual,
    })),
    ...[...PRE_COMMITTEE, LOAN_STATUS.committee, LOAN_STATUS.approved].map((from) => ({
        from,
        to: LOAN_STATUS.cancelled,
        label: 'Anular (desistimiento)',
        permission: 'loans:cancel',
        trigger: TRANSITION_TRIGGER.manual,
    })),
];
/** Regla de la transición `from → to`, si existe. */
export function findTransition(from, to) {
    return TRANSITIONS.find((t) => t.from === from && t.to === to);
}
/** Transiciones que se ofrecen a un usuario para un préstamo, con los motivos de bloqueo. */
export function availableTransitions(status, facts, permissions) {
    return TRANSITIONS.filter((t) => t.from === status && t.trigger !== TRANSITION_TRIGGER.payment)
        .filter((t) => !(t.to === LOAN_STATUS.appraisal && !facts.requiresCollateral))
        .filter((t) => !(t.from === LOAN_STATUS.document_review && t.to === LOAN_STATUS.credit_analysis && facts.requiresCollateral))
        .map((t) => {
        const blockers = [...(t.missing?.(facts) ?? [])];
        if (!permissions.includes(t.permission))
            blockers.push('No tienes permiso para esta acción');
        return { to: t.to, label: t.label, trigger: t.trigger, allowed: blockers.length === 0, blockers };
    });
}
//# sourceMappingURL=lifecycle.js.map